import { Injectable } from '@angular/core';
import { HubConnectionBuilder, LogLevel } from '@aspnet/signalr';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { AuthenticationService } from './AuthenticationService';

export interface TaskCenterNotificationSnapshot {
  PayoutCount: number;
  CertificateCount: number;
  DocumentCount: number;
  ActionRequiredCount: number;
  CollectionCaseCount: number;
  HasPendingPayouts?: boolean;
  HasPendingCertificates?: boolean;
  HasPendingDocuments?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TaskCenterNotificationPushService {
  private refresh: (() => void) | null = null;
  constructor(private auth: AuthenticationService) { }
  requestRefresh(): void { this.refresh?.(); }

  watch(): Observable<TaskCenterNotificationSnapshot> {
    return new Observable(observer => {
      const apiBase = environment.hostApi.replace(/\/api\/?$/, '');
      const hub = new HubConnectionBuilder().withUrl(`${apiBase}/hubs/task-center-notifications`, {
        accessTokenFactory: () => this.auth.token
      }).configureLogging(LogLevel.Warning).build();
      let stopped = false;
      let connected = false;
      let retry: ReturnType<typeof setTimeout>;
      const schedule = () => {
        connected = false;
        clearTimeout(retry);
        if (!stopped) retry = setTimeout(connect, 5000);
      };
      const connect = async () => {
        if (stopped) return;
        try {
          await hub.start();
          if (stopped) { await hub.stop(); return; }
          connected = true;
          // The server sends a shared snapshot on every connection/reconnection.
        } catch (error) {
          if (stopped) return;
          if (error.statusCode === 401 || error.statusCode === 403) {
            observer.error({ status: error.statusCode });
            return;
          }
          await hub.stop().catch(() => undefined);
          schedule();
        }
      };
      const refresh = () => {
        if (connected && !stopped) void hub.invoke('Refresh').catch(() => undefined);
      };
      this.refresh = refresh;
      hub.on('TaskCenterNotifications', state => observer.next({
        PayoutCount: state.PayoutCount ?? state.payoutCount,
        CertificateCount: state.CertificateCount ?? state.certificateCount,
        DocumentCount: state.DocumentCount ?? state.documentCount,
        ActionRequiredCount: state.ActionRequiredCount ?? state.actionRequiredCount,
        CollectionCaseCount: state.CollectionCaseCount ?? state.collectionCaseCount,
        HasPendingPayouts: state.HasPendingPayouts ?? state.hasPendingPayouts,
        HasPendingCertificates: state.HasPendingCertificates ?? state.hasPendingCertificates,
        HasPendingDocuments: state.HasPendingDocuments ?? state.hasPendingDocuments
      }));
      hub.onclose(schedule);
      void connect();
      return () => {
        stopped = true;
        clearTimeout(retry);
        if (this.refresh === refresh) this.refresh = null;
        hub.off('TaskCenterNotifications');
        void hub.stop().catch(() => undefined);
      };
    });
  }
}
